import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  getAnalyticsSummary,
  getPageVisits,
  getCancellations,
  AnalyticsSummary,
  PageVisit,
  CancellationEvent,
} from '../services/analyticsService';

// Admin email whitelist - must match server ADMIN_EMAILS env var
const ADMIN_EMAILS = ['woloshindima@gmail.com'];

type UserType = 'free' | 'subscriber' | 'admin' | 'investor';

interface AdminUser {
  userId: string;
  email: string;
  plan: string;
  status: string;
  type: UserType[];
  promo: string | null;
  createdAt: string | null;
  lastSignIn: string | null;
}

interface Investor {
  id: string;
  userId: string;
  email: string;
  name: string;
  promoCode: string;
  status: string;
  referralCount: number;
  activeReferrals: number;
  totalEarnings: number;
  createdAt: string;
}

interface LogEntry {
  id: string;
  type: 'log' | 'error';
  level?: string;
  event?: string;
  message?: string;
  userEmail?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

type Tab = 'users' | 'investors' | 'logs' | 'analytics' | 'events';

interface EarlyBirdConfig {
  name: string;
  startDate: string;
  endDate: string;
  isEnabled: boolean;
  discount: number;
  grantedFeatures: string[];
  updatedAt?: string;
  updatedBy?: string;
}

const PLANS = ['free', 'basic', 'pro', 'pro_interview', 'ielts', 'pro_ielts', 'interview'];
const STATUSES = ['active', 'canceled', 'past_due', 'trialing', 'incomplete'];
const INVESTOR_STATUSES = ['pending', 'approved', 'suspended'];
const USER_TYPES: UserType[] = ['free', 'subscriber', 'admin', 'investor'];

const AdminPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('users');

  // Users state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  // Investors state
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [investorsLoading, setInvestorsLoading] = useState(false);
  const [investorsError, setInvestorsError] = useState<string | null>(null);

  // Logs state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  // User edit modal state
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editPlan, setEditPlan] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editType, setEditType] = useState<UserType[]>([]);
  const [editPromo, setEditPromo] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // User delete modal state
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Investor edit modal state
  const [editingInvestor, setEditingInvestor] = useState<Investor | null>(null);
  const [editInvestorStatus, setEditInvestorStatus] = useState('');
  const [editInvestorLoading, setEditInvestorLoading] = useState(false);

  // Investor delete modal state
  const [deletingInvestor, setDeletingInvestor] = useState<Investor | null>(null);
  const [deleteInvestorLoading, setDeleteInvestorLoading] = useState(false);

  // Log delete state
  const [deletingLog, setDeletingLog] = useState<LogEntry | null>(null);
  const [deleteLogLoading, setDeleteLogLoading] = useState(false);

  // User stats modal state
  const [viewingStatsUser, setViewingStatsUser] = useState<AdminUser | null>(null);
  const [userStats, setUserStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Log level filter state
  const [logLevelFilter, setLogLevelFilter] = useState<'all' | 'error' | 'warning' | 'info'>('all');

  // Analytics state
  const [analyticsSummary, setAnalyticsSummary] = useState<AnalyticsSummary | null>(null);
  const [pageVisits, setPageVisits] = useState<PageVisit[]>([]);
  const [cancellations, setCancellations] = useState<CancellationEvent[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [analyticsView, setAnalyticsView] = useState<'summary' | 'pages' | 'cancellations'>('summary');

  // Events state (EarlyBird)
  const [earlyBirdConfig, setEarlyBirdConfig] = useState<EarlyBirdConfig | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [editingEarlyBird, setEditingEarlyBird] = useState(false);
  const [earlyBirdEndDate, setEarlyBirdEndDate] = useState('');
  const [earlyBirdEnabled, setEarlyBirdEnabled] = useState(true);
  const [savingEarlyBird, setSavingEarlyBird] = useState(false);

  // Users chart date range (default to current week)
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  };
  const [usersChartStartDate, setUsersChartStartDate] = useState(getDefaultDateRange().start);
  const [usersChartEndDate, setUsersChartEndDate] = useState(getDefaultDateRange().end);

  // Selected page for chart
  const [selectedPageForChart, setSelectedPageForChart] = useState<string | null>(null);

  // Check if user is admin
  const isAdmin = currentUser?.email &&
    ADMIN_EMAILS.map(e => e.toLowerCase()).includes(currentUser.email.toLowerCase());

  // Calculate users per day for chart
  const usersPerDayData = useMemo(() => {
    if (!users.length || !usersChartStartDate || !usersChartEndDate) return [];

    const startDate = new Date(usersChartStartDate);
    const endDate = new Date(usersChartEndDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Create all dates in range (to show zeros for days with no signups)
    const dateRange: string[] = [];
    const current = new Date(startDate);
    while (current <= endDate) {
      dateRange.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    const countsByDate: Record<string, number> = {};

    // Initialize all dates with 0
    dateRange.forEach((date) => {
      countsByDate[date] = 0;
    });

    users.forEach((user) => {
      if (user.createdAt) {
        const userDate = new Date(user.createdAt);
        if (userDate >= startDate && userDate <= endDate) {
          const date = userDate.toISOString().split('T')[0];
          countsByDate[date] = (countsByDate[date] || 0) + 1;
        }
      }
    });

    const sortedDates = Object.keys(countsByDate).sort();

    return sortedDates.map((date) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      users: countsByDate[date],
    }));
  }, [users, usersChartStartDate, usersChartEndDate]);

  // Calculate page visits per day for selected page
  const pageVisitsPerDayData = useMemo(() => {
    if (!selectedPageForChart || !pageVisits.length) return [];

    const filteredVisits = pageVisits.filter((v) => v.page === selectedPageForChart);
    if (!filteredVisits.length) return [];

    // Get date range from the visits
    const timestamps = filteredVisits.map((v) => new Date(v.timestamp).getTime());
    const minDate = new Date(Math.min(...timestamps));
    const maxDate = new Date(Math.max(...timestamps));
    minDate.setHours(0, 0, 0, 0);
    maxDate.setHours(23, 59, 59, 999);

    // Create all dates in range
    const dateRange: string[] = [];
    const current = new Date(minDate);
    while (current <= maxDate) {
      dateRange.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    const countsByDate: Record<string, number> = {};
    dateRange.forEach((date) => {
      countsByDate[date] = 0;
    });

    filteredVisits.forEach((visit) => {
      const date = new Date(visit.timestamp).toISOString().split('T')[0];
      countsByDate[date] = (countsByDate[date] || 0) + 1;
    });

    const sortedDates = Object.keys(countsByDate).sort();

    return sortedDates.map((date) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      visits: countsByDate[date],
    }));
  }, [selectedPageForChart, pageVisits]);

  // Helper to get auth headers
  const getAuthHeaders = useCallback(async (): Promise<HeadersInit> => {
    if (!currentUser) return {};
    const token = await currentUser.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, [currentUser]);

  // Load all data when admin accesses the page
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadInvestors();
      loadLogs();
      loadAnalytics();
      loadEarlyBirdConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/admin/list`, { headers });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsersError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadInvestors = async () => {
    setInvestorsLoading(true);
    setInvestorsError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/admin/investors`, { headers });
      if (!response.ok) throw new Error('Failed to fetch investors');
      const data = await response.json();
      setInvestors(data);
    } catch (err) {
      console.error('Error loading investors:', err);
      setInvestorsError('Failed to load investors');
    } finally {
      setInvestorsLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/admin/logs?limit=200`, { headers });
      if (!response.ok) throw new Error('Failed to fetch logs');
      const data = await response.json();
      setLogs(data);
    } catch (err) {
      console.error('Error loading logs:', err);
      setLogsError('Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const [summary, visits, cancels] = await Promise.all([
        getAnalyticsSummary(),
        getPageVisits(100),
        getCancellations(50),
      ]);
      setAnalyticsSummary(summary);
      setPageVisits(visits);
      setCancellations(cancels);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setAnalyticsError('Failed to load analytics. Backend endpoints may not be configured yet.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadEarlyBirdConfig = async () => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/early-bird`);
      if (!response.ok) throw new Error('Failed to fetch EarlyBird config');
      const data = await response.json();
      setEarlyBirdConfig(data);
      setEarlyBirdEndDate(data.endDate.split('T')[0]);
      setEarlyBirdEnabled(data.isEnabled);
    } catch (err) {
      console.error('Error loading EarlyBird config:', err);
      setEventsError('Failed to load event configuration');
    } finally {
      setEventsLoading(false);
    }
  };

  const handleExtendEarlyBird = async (days: number) => {
    if (!earlyBirdConfig) return;
    setSavingEarlyBird(true);
    try {
      const currentEnd = new Date(earlyBirdConfig.endDate);
      const newEnd = new Date(currentEnd.getTime() + days * 24 * 60 * 60 * 1000);

      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/early-bird`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ endDate: newEnd.toISOString() }),
      });
      if (!response.ok) throw new Error('Failed to update');
      await loadEarlyBirdConfig();
      alert(`Extended by ${days} days!`);
    } catch (err) {
      console.error('Error extending EarlyBird:', err);
      alert('Failed to extend event');
    } finally {
      setSavingEarlyBird(false);
    }
  };

  const handleSaveEarlyBird = async () => {
    if (!earlyBirdConfig) return;
    setSavingEarlyBird(true);
    try {
      const headers = await getAuthHeaders();
      const newEndDate = new Date(earlyBirdEndDate + 'T23:59:59Z');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/early-bird`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          endDate: newEndDate.toISOString(),
          isEnabled: earlyBirdEnabled,
        }),
      });
      if (!response.ok) throw new Error('Failed to update');
      await loadEarlyBirdConfig();
      setEditingEarlyBird(false);
      alert('Event updated successfully!');
    } catch (err) {
      console.error('Error saving EarlyBird:', err);
      alert('Failed to save event');
    } finally {
      setSavingEarlyBird(false);
    }
  };

  // User handlers
  const handleEditClick = (user: AdminUser) => {
    setEditingUser(user);
    setEditPlan(user.plan);
    setEditStatus(user.status);
    setEditType(user.type || ['free']);
    setEditPromo(user.promo || '');
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    setEditLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/admin/${editingUser.userId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            plan: editPlan,
            status: editStatus,
            type: editType,
            promo: editPromo || null,
          }),
        }
      );
      if (!response.ok) throw new Error('Failed to update user');
      setUsers(users.map(u =>
        u.userId === editingUser.userId
          ? { ...u, plan: editPlan, status: editStatus, type: editType, promo: editPromo || null }
          : u
      ));
      setEditingUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (user: AdminUser) => {
    setDeletingUser(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/admin/${deletingUser.userId}`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error('Failed to delete user');
      setUsers(users.filter(u => u.userId !== deletingUser.userId));
      setDeletingUser(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Investor handlers
  const handleEditInvestorClick = (investor: Investor) => {
    setEditingInvestor(investor);
    setEditInvestorStatus(investor.status);
  };

  const handleEditInvestorSave = async () => {
    if (!editingInvestor) return;
    setEditInvestorLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/admin/investors/${editingInvestor.id}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ status: editInvestorStatus }),
        }
      );
      if (!response.ok) throw new Error('Failed to update investor');
      setInvestors(investors.map(inv =>
        inv.id === editingInvestor.id ? { ...inv, status: editInvestorStatus } : inv
      ));
      setEditingInvestor(null);
    } catch (err) {
      console.error('Error updating investor:', err);
      alert('Failed to update investor');
    } finally {
      setEditInvestorLoading(false);
    }
  };

  const handleDeleteInvestorClick = (investor: Investor) => {
    setDeletingInvestor(investor);
  };

  const handleDeleteInvestorConfirm = async () => {
    if (!deletingInvestor) return;
    setDeleteInvestorLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/admin/investors/${deletingInvestor.id}`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error('Failed to delete investor');
      setInvestors(investors.filter(inv => inv.id !== deletingInvestor.id));
      setDeletingInvestor(null);
    } catch (err) {
      console.error('Error deleting investor:', err);
      alert('Failed to delete investor');
    } finally {
      setDeleteInvestorLoading(false);
    }
  };

  // Log handlers
  const handleDeleteLogClick = (log: LogEntry) => {
    setDeletingLog(log);
  };

  const handleDeleteLogConfirm = async () => {
    if (!deletingLog) return;
    setDeleteLogLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/admin/logs/${deletingLog.id}?type=${deletingLog.type}`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error('Failed to delete log');
      setLogs(logs.filter(l => l.id !== deletingLog.id));
      setDeletingLog(null);
    } catch (err) {
      console.error('Error deleting log:', err);
      alert('Failed to delete log');
    } finally {
      setDeleteLogLoading(false);
    }
  };

  // User stats handlers
  const handleViewStats = async (user: AdminUser) => {
    setViewingStatsUser(user);
    setStatsLoading(true);
    setUserStats(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/admin/${user.userId}/stats`,
        { headers }
      );
      if (!response.ok) throw new Error('Failed to fetch user stats');
      const data = await response.json();
      setUserStats(data);
    } catch (err) {
      console.error('Error loading user stats:', err);
      setUserStats({ error: 'Failed to load statistics' });
    } finally {
      setStatsLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format practice time to show minutes and seconds
  const formatPracticeTime = (minutes: number | undefined): string => {
    if (!minutes && minutes !== 0) return '0 sec';

    const totalSeconds = Math.round(minutes * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    if (mins === 0) {
      return `${secs} sec`;
    } else if (secs === 0) {
      return `${mins} min`;
    } else {
      return `${mins} min ${secs} sec`;
    }
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'pro_interview':
      case 'pro_ielts':
        return 'bg-purple-100 text-purple-800';
      case 'pro':
        return 'bg-blue-100 text-blue-800';
      case 'basic':
        return 'bg-green-100 text-green-800';
      case 'ielts':
      case 'interview':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'trialing':
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'canceled':
      case 'suspended':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLogLevelColor = (level: string, type: string) => {
    if (type === 'error') return 'bg-red-100 text-red-800';
    switch (level) {
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-orange-100 text-orange-800';
      case 'info':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeColor = (type: UserType) => {
    switch (type) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'investor':
        return 'bg-purple-100 text-purple-800';
      case 'subscriber':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleUserType = (type: UserType) => {
    if (editType.includes(type)) {
      setEditType(editType.filter(t => t !== type));
    } else {
      setEditType([...editType, type]);
    }
  };

  const renderLoading = () => (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
    </div>
  );

  const renderError = (error: string, retry: () => void) => (
    <div className="text-center py-12">
      <p className="text-red-600 mb-4">{error}</p>
      <button onClick={retry} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
        Retry
      </button>
    </div>
  );

  const renderUsersTab = () => {
    if (usersLoading) return renderLoading();
    if (usersError) return renderError(usersError, loadUsers);

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Email</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Plan</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Status</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Type</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Promo</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Created</th>
              <th className="text-center py-4 px-6 text-gray-600 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.userId} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-6 text-gray-900">{user.email}</td>
                <td className="py-4 px-6">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadgeColor(user.plan)}`}>
                    {user.plan}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(user.status)}`}>
                    {user.status}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <div className="flex flex-wrap gap-1">
                    {(user.type || ['free']).map((t) => (
                      <span key={t} className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(t)}`}>
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-4 px-6 text-gray-600">
                  {user.promo ? (
                    <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">{user.promo}</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="py-4 px-6 text-gray-600">{formatDate(user.createdAt)}</td>
                <td className="py-4 px-6">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => handleViewStats(user)} className="px-3 py-1 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200" title="View Statistics">
                      Info
                    </button>
                    <button onClick={() => handleEditClick(user)} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                      Edit
                    </button>
                    <button onClick={() => handleDeleteClick(user)} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderInvestorsTab = () => {
    if (investorsLoading) return renderLoading();
    if (investorsError) return renderError(investorsError, loadInvestors);

    if (investors.length === 0) {
      return <div className="p-8 text-center text-gray-500">No investors found.</div>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Name</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Email</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Promo Code</th>
              <th className="text-left py-4 px-6 text-gray-600 font-medium">Status</th>
              <th className="text-right py-4 px-6 text-gray-600 font-medium">Referrals</th>
              <th className="text-right py-4 px-6 text-gray-600 font-medium">Earnings</th>
              <th className="text-center py-4 px-6 text-gray-600 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {investors.map((inv) => (
              <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-6 text-gray-900 font-medium">{inv.name}</td>
                <td className="py-4 px-6 text-gray-600">{inv.email}</td>
                <td className="py-4 px-6">
                  <span className="font-mono bg-gray-100 px-2 py-1 rounded">{inv.promoCode}</span>
                </td>
                <td className="py-4 px-6">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(inv.status)}`}>
                    {inv.status}
                  </span>
                </td>
                <td className="py-4 px-6 text-right text-gray-900">{inv.referralCount}</td>
                <td className="py-4 px-6 text-right text-green-600 font-medium">${inv.totalEarnings.toFixed(2)}</td>
                <td className="py-4 px-6">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => handleEditInvestorClick(inv)} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                      Edit
                    </button>
                    <button onClick={() => handleDeleteInvestorClick(inv)} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderLogsTab = () => {
    if (logsLoading) return renderLoading();
    if (logsError) return renderError(logsError, loadLogs);

    if (logs.length === 0) {
      return <div className="p-8 text-center text-gray-500">No logs found.</div>;
    }

    // Filter logs based on selected level
    const filteredLogs = logs.filter((log) => {
      if (logLevelFilter === 'all') return true;
      const logLevel = log.type === 'error' ? 'error' : (log.level || 'info');
      return logLevel === logLevelFilter;
    });

    // Count logs by level for filter badges
    const errorCount = logs.filter(l => l.type === 'error' || l.level === 'error').length;
    const warningCount = logs.filter(l => l.level === 'warning').length;
    const infoCount = logs.filter(l => l.type !== 'error' && l.level !== 'error' && l.level !== 'warning').length;

    return (
      <div>
        {/* Log Level Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 mr-2">Filter by level:</span>
            <button
              onClick={() => setLogLevelFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                logLevelFilter === 'all'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All ({logs.length})
            </button>
            <button
              onClick={() => setLogLevelFilter('error')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                logLevelFilter === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              Error ({errorCount})
            </button>
            <button
              onClick={() => setLogLevelFilter('warning')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                logLevelFilter === 'warning'
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              Warning ({warningCount})
            </button>
            <button
              onClick={() => setLogLevelFilter('info')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                logLevelFilter === 'info'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              Info ({infoCount})
            </button>
          </div>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No logs found for selected filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-600 font-medium">Time</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-medium">Level</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-medium">Event/Message</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-medium">User</th>
                  <th className="text-center py-4 px-6 text-gray-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6 text-gray-600 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLogLevelColor(log.level || '', log.type)}`}>
                        {log.type === 'error' ? 'ERROR' : log.level?.toUpperCase() || 'INFO'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-gray-900 font-medium">{log.event || log.message || '-'}</div>
                      {log.metadata && (
                        <div className="text-gray-500 text-sm mt-1 max-w-md truncate">
                          {JSON.stringify(log.metadata)}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6 text-gray-600">{log.userEmail || '-'}</td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center">
                        <button onClick={() => handleDeleteLogClick(log)} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderAnalyticsTab = () => {
    if (analyticsLoading) return renderLoading();
    if (analyticsError) return renderError(analyticsError, loadAnalytics);

    return (
      <div>
        {/* Sub-navigation */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAnalyticsView('summary')}
              className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                analyticsView === 'summary'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Summary
            </button>
            <button
              onClick={() => setAnalyticsView('pages')}
              className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                analyticsView === 'pages'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Page Visits ({pageVisits.length})
            </button>
            <button
              onClick={() => setAnalyticsView('cancellations')}
              className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                analyticsView === 'cancellations'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              Cancellations ({cancellations.length})
            </button>
          </div>
        </div>

        {/* Summary View */}
        {analyticsView === 'summary' && analyticsSummary && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs text-blue-600 uppercase tracking-wide">Total Visits</p>
                <p className="text-3xl font-bold text-blue-900">{analyticsSummary.totalVisits || 0}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-xs text-emerald-600 uppercase tracking-wide">Unique Visitors</p>
                <p className="text-3xl font-bold text-emerald-900">{analyticsSummary.uniqueVisitors || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="text-xs text-purple-600 uppercase tracking-wide">Total Events</p>
                <p className="text-3xl font-bold text-purple-900">
                  {(analyticsSummary.events || []).reduce((sum, e) => sum + e.count, 0)}
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-4">
                <p className="text-xs text-red-600 uppercase tracking-wide">Cancellations</p>
                <p className="text-3xl font-bold text-red-900">{cancellations.length}</p>
              </div>
            </div>

            {/* Users Per Day Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="text-lg font-semibold text-gray-900">New Users Per Day</h3>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">From:</label>
                  <input
                    type="date"
                    value={usersChartStartDate}
                    onChange={(e) => setUsersChartStartDate(e.target.value)}
                    max={usersChartEndDate}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <label className="text-sm text-gray-600">To:</label>
                  <input
                    type="date"
                    value={usersChartEndDate}
                    onChange={(e) => setUsersChartEndDate(e.target.value)}
                    min={usersChartStartDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
              <div className="h-64">
                {usersPerDayData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={usersPerDayData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        }}
                        labelStyle={{ color: '#374151', fontWeight: 600 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="users"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, fill: '#059669' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500">
                    No user data available for this period
                  </div>
                )}
              </div>
            </div>

            {/* Top Pages */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Pages</h3>
              {(analyticsSummary.topPages || []).length > 0 ? (
                <div className="space-y-2">
                  {analyticsSummary.topPages.map((page, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedPageForChart(selectedPageForChart === page.page ? null : page.page)}
                      className={`w-full flex items-center justify-between py-2 px-3 rounded-lg border-b border-gray-100 last:border-0 transition-colors ${
                        selectedPageForChart === page.page
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className={`${selectedPageForChart === page.page ? 'text-emerald-700' : 'text-gray-700'}`}>
                        {page.page}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${selectedPageForChart === page.page ? 'text-emerald-700' : 'text-gray-900'}`}>
                          {page.visits} visits
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform ${selectedPageForChart === page.page ? 'rotate-180 text-emerald-600' : 'text-gray-400'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No page visit data yet</p>
              )}

              {/* Page Visits Chart */}
              {selectedPageForChart && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-800 mb-3">
                    Visits for "{selectedPageForChart}"
                  </h4>
                  <div className="h-48">
                    {pageVisitsPerDayData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={pageVisitsPerDayData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: '#6b7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#6b7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            }}
                            labelStyle={{ color: '#374151', fontWeight: 600 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="visits"
                            stroke="#8b5cf6"
                            strokeWidth={2}
                            dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, fill: '#7c3aed' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500">
                        No visit data available for this page
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Page Visits View */}
        {analyticsView === 'pages' && (
          <div className="overflow-x-auto">
            {pageVisits.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No page visits recorded yet.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Time</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Page</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Path</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">User</th>
                  </tr>
                </thead>
                <tbody>
                  {pageVisits.map((visit, index) => (
                    <tr key={visit.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 text-gray-600 whitespace-nowrap">
                        {formatDate(visit.timestamp?.toString() || '')}
                      </td>
                      <td className="py-4 px-6 text-gray-900 font-medium">{visit.page}</td>
                      <td className="py-4 px-6 text-gray-600 font-mono text-sm">{visit.path}</td>
                      <td className="py-4 px-6 text-gray-600">{visit.userEmail || 'Anonymous'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Cancellations View */}
        {analyticsView === 'cancellations' && (
          <div className="overflow-x-auto">
            {cancellations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No cancellations recorded yet.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Time</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">User</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Plan</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Reason</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {cancellations.map((cancel, index) => (
                    <tr key={cancel.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 text-gray-600 whitespace-nowrap">
                        {formatDate(cancel.timestamp?.toString() || '')}
                      </td>
                      <td className="py-4 px-6 text-gray-900">{cancel.userEmail}</td>
                      <td className="py-4 px-6">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadgeColor(cancel.plan)}`}>
                          {cancel.plan}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-600">{cancel.reason || '-'}</td>
                      <td className="py-4 px-6 text-gray-600 max-w-xs truncate">{cancel.feedback || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* No summary data message */}
        {analyticsView === 'summary' && !analyticsSummary && (
          <div className="p-8 text-center text-gray-500">No analytics data available yet.</div>
        )}
      </div>
    );
  };

  const renderEventsTab = () => {
    if (eventsLoading) return renderLoading();
    if (eventsError) return renderError(eventsError, loadEarlyBirdConfig);

    if (!earlyBirdConfig) {
      return <div className="p-8 text-center text-gray-500">No event configuration found.</div>;
    }

    const now = new Date();
    const endDate = new Date(earlyBirdConfig.endDate);
    const startDate = new Date(earlyBirdConfig.startDate);
    const isActive = earlyBirdConfig.isEnabled && now >= startDate && now <= endDate;
    const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

    return (
      <div className="p-6">
        {/* Early Bird Event Card */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎉</span>
              <div>
                <h3 className="text-xl font-bold">{earlyBirdConfig.name}</h3>
                <p className="text-purple-100 text-sm">Promotional Event</p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isActive ? 'bg-green-400 text-green-900' : 'bg-gray-300 text-gray-700'
            }`}>
              {isActive ? 'ACTIVE' : earlyBirdConfig.isEnabled ? 'ENDED' : 'DISABLED'}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/20 rounded-xl p-3">
              <p className="text-purple-100 text-xs uppercase">Start Date</p>
              <p className="font-semibold">{startDate.toLocaleDateString()}</p>
            </div>
            <div className="bg-white/20 rounded-xl p-3">
              <p className="text-purple-100 text-xs uppercase">End Date</p>
              <p className="font-semibold">{endDate.toLocaleDateString()}</p>
            </div>
            <div className="bg-white/20 rounded-xl p-3">
              <p className="text-purple-100 text-xs uppercase">Days Remaining</p>
              <p className="font-semibold text-2xl">{isActive ? daysRemaining : 0}</p>
            </div>
            <div className="bg-white/20 rounded-xl p-3">
              <p className="text-purple-100 text-xs uppercase">Discount</p>
              <p className="font-semibold text-2xl">{earlyBirdConfig.discount}%</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleExtendEarlyBird(1)}
              disabled={savingEarlyBird}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
            >
              +1 Day
            </button>
            <button
              onClick={() => handleExtendEarlyBird(3)}
              disabled={savingEarlyBird}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
            >
              +3 Days
            </button>
            <button
              onClick={() => handleExtendEarlyBird(7)}
              disabled={savingEarlyBird}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
            >
              +7 Days
            </button>
            <button
              onClick={() => setEditingEarlyBird(true)}
              className="px-4 py-2 bg-white text-purple-600 hover:bg-purple-50 rounded-lg font-medium text-sm transition-colors ml-auto"
            >
              Edit Event
            </button>
          </div>
        </div>

        {/* Event Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Event Details</h4>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Status</p>
              <p className="font-medium">{earlyBirdConfig.isEnabled ? 'Enabled' : 'Disabled'}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Features Granted to Free Users</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {earlyBirdConfig.grantedFeatures.map((feature) => (
                  <span key={feature} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                    {feature.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>

            {earlyBirdConfig.updatedAt && (
              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Last updated: {formatDate(earlyBirdConfig.updatedAt)}
                  {earlyBirdConfig.updatedBy && ` by ${earlyBirdConfig.updatedBy}`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Edit Modal */}
        {editingEarlyBird && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Early Bird Event</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={earlyBirdEndDate}
                    onChange={(e) => setEarlyBirdEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={earlyBirdEnabled}
                      onChange={(e) => setEarlyBirdEnabled(e.target.checked)}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Event Enabled</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    When disabled, the banner won't show and features won't be unlocked
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setEditingEarlyBird(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={savingEarlyBird}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEarlyBird}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  disabled={savingEarlyBird}
                >
                  {savingEarlyBird ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Show login prompt if not logged in
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin Access Required</h2>
          <p className="text-gray-600 mb-6">Please log in to access the admin panel.</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-2">You don't have permission to access this page.</p>
          <p className="text-sm text-gray-500 mb-6">Logged in as: {currentUser.email}</p>
          <a
            href="/app"
            className="inline-block px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
          >
            Go to App
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="Go to Home"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="hidden sm:inline">Home</span>
            </Link>
            <Link
              to="/app"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              title="Go to App"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              <span className="hidden sm:inline">Practice</span>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'users'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Users ({users.length})
              </button>
              <button
                onClick={() => setActiveTab('investors')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'investors'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Investors ({investors.length})
              </button>
              <button
                onClick={() => setActiveTab('logs')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'logs'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Logs ({logs.length})
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'analytics'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Analytics
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'events'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Events
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'users' && renderUsersTab()}
          {activeTab === 'investors' && renderInvestorsTab()}
          {activeTab === 'logs' && renderLogsTab()}
          {activeTab === 'analytics' && renderAnalyticsTab()}
          {activeTab === 'events' && renderEventsTab()}
        </div>
      </div>

      {/* User Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit User</h2>
            <p className="text-gray-600 mb-4">{editingUser.email}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  value={editPlan}
                  onChange={(e) => setEditPlan(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {PLANS.map((plan) => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
                <div className="flex flex-wrap gap-2">
                  {USER_TYPES.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleUserType(type)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        editType.includes(type)
                          ? getTypeBadgeColor(type)
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code</label>
                <input
                  type="text"
                  value={editPromo}
                  onChange={(e) => setEditPromo(e.target.value.toUpperCase())}
                  placeholder="e.g., FRIEND20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-gray-500 mt-1">Referral code that brought this user</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditingUser(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={editLoading}>
                Cancel
              </button>
              <button onClick={handleEditSave} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50" disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Delete Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete User</h2>
            <p className="text-gray-600 mb-2">Are you sure you want to delete this user?</p>
            <p className="text-gray-900 font-medium mb-4">{deletingUser.email}</p>
            <p className="text-red-600 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeletingUser(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={deleteLoading}>
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investor Edit Modal */}
      {editingInvestor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Investor</h2>
            <p className="text-gray-600 mb-2">{editingInvestor.name}</p>
            <p className="text-gray-500 text-sm mb-4">{editingInvestor.email}</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={editInvestorStatus}
                onChange={(e) => setEditInvestorStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {INVESTOR_STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditingInvestor(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={editInvestorLoading}>
                Cancel
              </button>
              <button onClick={handleEditInvestorSave} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50" disabled={editInvestorLoading}>
                {editInvestorLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investor Delete Modal */}
      {deletingInvestor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Investor</h2>
            <p className="text-gray-600 mb-2">Are you sure you want to delete this investor?</p>
            <p className="text-gray-900 font-medium mb-4">{deletingInvestor.name} ({deletingInvestor.email})</p>
            <p className="text-red-600 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeletingInvestor(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={deleteInvestorLoading}>
                Cancel
              </button>
              <button onClick={handleDeleteInvestorConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" disabled={deleteInvestorLoading}>
                {deleteInvestorLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Delete Modal */}
      {deletingLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Log</h2>
            <p className="text-gray-600 mb-2">Are you sure you want to delete this log entry?</p>
            <p className="text-gray-900 font-medium mb-4">{deletingLog.event || deletingLog.message || 'Log entry'}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeletingLog(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={deleteLogLoading}>
                Cancel
              </button>
              <button onClick={handleDeleteLogConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" disabled={deleteLogLoading}>
                {deleteLogLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Stats Modal */}
      {viewingStatsUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">User Statistics</h2>
              <button
                onClick={() => setViewingStatsUser(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-gray-600 mb-6">{viewingStatsUser.email}</p>

            {statsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
              </div>
            ) : userStats?.error ? (
              <div className="text-center py-8 text-red-600">{userStats.error}</div>
            ) : userStats ? (
              <div className="space-y-6">
                {/* Account Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Account Info</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Plan</p>
                      <p className="font-medium text-gray-900">{viewingStatsUser.plan}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <p className="font-medium text-gray-900">{viewingStatsUser.status}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="font-medium text-gray-900">{formatDate(viewingStatsUser.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Last Sign In</p>
                      <p className="font-medium text-gray-900">{formatDate(viewingStatsUser.lastSignIn)}</p>
                    </div>
                  </div>
                </div>

                {/* Practice Statistics */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-blue-700 mb-3 uppercase tracking-wide">Practice Statistics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-blue-600">Total Sessions</p>
                      <p className="text-2xl font-bold text-blue-900">{userStats.totalSessions || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Total Recordings</p>
                      <p className="text-2xl font-bold text-blue-900">{userStats.totalRecordings || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Practice Time</p>
                      <p className="text-2xl font-bold text-blue-900">{formatPracticeTime(userStats.totalPracticeMinutes)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Current Streak</p>
                      <p className="text-2xl font-bold text-blue-900">{userStats.currentStreak || 0} days</p>
                    </div>
                  </div>
                </div>

                {/* Activity Breakdown */}
                <div className="bg-purple-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-purple-700 mb-3 uppercase tracking-wide">Activity Breakdown</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-purple-600">Speech</p>
                      <p className="text-xl font-bold text-purple-900">{userStats.speechSessions || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-purple-600">Interview</p>
                      <p className="text-xl font-bold text-purple-900">{userStats.interviewSessions || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-purple-600">IELTS</p>
                      <p className="text-xl font-bold text-purple-900">{userStats.ieltsSessions || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Daily Usage */}
                <div className="bg-emerald-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-emerald-700 mb-3 uppercase tracking-wide">Today's Usage</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-emerald-600">Recordings Today</p>
                      <p className="text-2xl font-bold text-emerald-900">{userStats.recordingsToday || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-emerald-600">Last Activity</p>
                      <p className="font-medium text-emerald-900">{userStats.lastActivity ? formatDate(userStats.lastActivity) : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Average Scores */}
                {(userStats.avgPronunciationScore || userStats.avgIeltsScore) && (
                  <div className="bg-orange-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-orange-700 mb-3 uppercase tracking-wide">Average Scores</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {userStats.avgPronunciationScore && (
                        <div>
                          <p className="text-xs text-orange-600">Pronunciation</p>
                          <p className="text-2xl font-bold text-orange-900">{userStats.avgPronunciationScore.toFixed(1)}%</p>
                        </div>
                      )}
                      {userStats.avgIeltsScore && (
                        <div>
                          <p className="text-xs text-orange-600">IELTS Band</p>
                          <p className="text-2xl font-bold text-orange-900">{userStats.avgIeltsScore.toFixed(1)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No statistics available</div>
            )}

            <div className="mt-6">
              <button
                onClick={() => setViewingStatsUser(null)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
